<template>
  <v-container
    class="rounded-lg elevation-3 d-flex flex-column px-4 pb-4"
    style="height: 100%; background-color: var(--color-bg)"
    fluid
  >
    <div class="d-flex justify-space-between align-center">
      <h3
        class="font-weight-light"
        style="color: var(--color-text)"
      >
        Speed Consumption Table
      </h3>

      <v-spacer />

      <!-- Excel export button -->
      <v-btn
        small
        height="28"
        class="mx-1 px-2"
        color="#439346"
        dark
        elevation="0"
        outlined
        @click="exportTableToExcel"
      >
        <v-icon
          left
          small
        >
          mdi-file-excel
        </v-icon>
        Export Excel
      </v-btn>

      <ThemedCheckbox
        v-model="showConsumption"
        class="pa-0 ma-0"
        dense
        :label="auxiliaryConsumptionLabel"
        :input-value="showConsumption"
      />
    </div>

    <div style="height: 1em" />

    <v-container
      style="overflow-x: auto !important;"
      class="pa-0 table-scroll-container"
    >
      <!-- Table wrapper with min-width to force horizontal scroll -->
      <div class="table-wrapper">
        <v-data-table
          :headers="tableHeaders"
          :items="tableItems"
          disable-pagination
          height="30vh"
          hide-default-footer
          hide-default-header
        >
          <template v-slot:header="{ props: { headers } }">
            <thead>
              <!-- Group header row with extended boxes -->
              <tr class="group-header-row">
                <!-- Basic Info (no box) -->
                <th
                  colspan="3"
                  class="group-header-basic"
                />

                <!-- KD Model Group Box -->
                <th
                  colspan="2"
                  class="group-header-kdmodel-box"
                >
                  <div class="group-header-content">
                    <v-icon small class="group-icon">mdi-calculator</v-icon>
                    <span class="group-title">KD Model Predicted FOC [tn/d]</span>
                  </div>
                </th>

                <!-- Vessel Data Group Box -->
                <th
                  colspan="2"
                  class="group-header-data-box"
                >
                  <div class="group-header-content">
                    <v-icon small class="group-icon">mdi-database</v-icon>
                    <span class="group-title">Data Messured FOC [tn/d]</span>
                  </div>
                </th>
              </tr>

              <!-- Column header row -->
              <tr class="column-header-row">
                <th
                  v-for="(header, idx) in headers"
                  :key="header.text"
                  :class="['table-header', getColumnClass(header.value)]"
                  :style="{ width: header.width, textAlign: header.align }"
                >
                  <v-tooltip
                    v-if="header.tooltip"
                    bottom
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <span
                        v-bind="attrs"
                        v-on="on"
                      >{{ header.text }}</span>
                    </template>
                    <span>{{ header.tooltip }}</span>
                  </v-tooltip>
                  <span v-else>{{ header.text }}</span>
                </th>
              </tr>
            </thead>
          </template>

          <!-- Custom item slot to apply group classes to cells -->
          <template v-slot:item="{ item, index }">
            <tr :class="['table-row', `row-${index % 2}`]">
              <td v-for="header in tableHeaders" :key="header.value" :class="getColumnClass(header.value)">
                {{ item[header.value] }}
              </td>
            </tr>
          </template>
        </v-data-table>
      </div>
    </v-container>
  </v-container>
</template>

<script>
import * as XLSX from "xlsx";
import { mapState, mapGetters } from "vuex";
import ThemedCheckbox from "@/core/ThemedCheckbox.vue";

export default {
  name: "ResultsTable",
  components: {
    ThemedCheckbox,
  },
  data() {
    return {
      showConsumption: true,
    };
  },
  computed: {
    ...mapState("kDiagram", ["calculationResult", "calculationData"]),
    ...mapGetters("kDiagram", [
      "speedsOfInterest",
      "powerOfInterest",
      "consumptionOfInterest",
      "rpmOfInterest",
      "isoDailyConsumptionOfInterest",
      "measuredIsoConsumptionOfInterest",
      "medianMeasuredConsumptionOfInterest",
      "auxiliaryConsumption",
    ]),

    tableItems() {
  if (!this.calculationResult) return []

  return this.rpmOfInterest.map((rpm, index) => {
    const auxValue = this.showConsumption ? this.auxiliaryConsumption : 0

    // Safe numeric extraction
    const isoDaily = this.isoDailyConsumptionOfInterest[index]
    const measuredIso = this.measuredIsoConsumptionOfInterest[index]
    const medianMeasured = this.medianMeasuredConsumptionOfInterest[index]
    const consumption = this.consumptionOfInterest[index]

    // Check if values are valid before adding aux
    const isValidNum = (v) => v !== null && v !== undefined && !Number.isNaN(Number(v)) && Number(v) !== 0

    return {
      rpm: this.formatValue(rpm, 1),
      speed: this.speedsOfInterest[index],
      power: this.formatValue(this.powerOfInterest[index], 1),

      // ISO-Predicted (KD Model)
      isoDailyConsumption: this.formatValue(isoDaily, 1),

      // Actual-Predicted (KD Model + aux)
      total: isValidNum(consumption)
        ? (Number(consumption) + auxValue).toFixed(1)
        : "-",

      // ISO-Corrected (Measured)
      measuredIsoConsumption: this.formatValue(measuredIso, 1),

      // Actual (Measured + aux)
      medianMeasuredConsumption: isValidNum(medianMeasured)
        ? (Number(medianMeasured) + auxValue).toFixed(1)
        : "-",
    }
  })
},

    tableHeaders() {
      return [
        {
          text: "Speed (kn)",
          value: "speed",
          align: "center",
          width: "80px",
          group: "basic",
        },
        {
          text: "RPM",
          value: "rpm",
          align: "center",
          width: "70px",
          group: "basic",
        },
        {
          text: "Power (kW)",
          value: "power",
          align: "center",
          width: "90px",
          group: "basic",
        },
        // KD Model group
        {
          text: "ISO Corrected FOC",
          value: "isoDailyConsumption",
          align: "center",
          width: "110px",
          tooltip: "KD Model's predicted Fuel Oil Consumption corrected based on Shop Test",
          group: "kdmodel",
        },
        {
          text: "Actual",
          value: "total",
          align: "center",
          width: "130px",
          tooltip: "KD Model's predicted Fuel Oil Consumption (with/without Aux consumption)",
          group: "kdmodel",
        },
        // Data group
        {
          text: "ISO Corrected FOC",
          value: "measuredIsoConsumption",
          align: "center",
          width: "140px",
          tooltip: "Average measured Fuel Oil Consumption corrected based on Shop Test",
          group: "data",
        },
        {
          text: "Actual",
          value: "medianMeasuredConsumption",
          align: "center",
          width: "150px",
          tooltip: "Average measured Fuel Oil Consumption (with/without Aux consumption)",
          group: "data",
        },
      ];
    },

    auxiliaryConsumptionLabel() {
      return `Include aux. cons. of ${this.auxiliaryConsumption} (tn/day)`;
    },
  },

  methods: {

    /**
   * Format a numeric value, returning "-" if null/zero/invalid
   */
    formatValue(value, decimals = 1) {
      // Handle null, undefined
      if (value === null || value === undefined) return "-"

      // Convert to number
      const num = Number(value)

      // Handle NaN (e.g., value was a non-numeric string)
      if (Number.isNaN(num)) return "-"

      // Handle zero (treat as "no data")
      if (num === 0) return "-"

      return num.toFixed(decimals)
    },
    getColumnClass(value) {
      // Return class based on column group for styling
      const header = this.tableHeaders.find((h) => h.value === value);
      if (!header) return "";
      return `group-${header.group}`;
    },

    exportTableToExcel() {
      if (!this.tableItems.length || !this.calculationData) return;

      const vessel =
        this.$route && this.$route.params && this.$route.params.vessel
          ? this.$route.params.vessel
          : "Vessel";

      const now = new Date();
      const exportTime =
        now.getUTCFullYear() +
        "-" +
        String(now.getUTCMonth() + 1).padStart(2, "0") +
        "-" +
        String(now.getUTCDate()).padStart(2, "0") +
        " " +
        String(now.getUTCHours()).padStart(2, "0") +
        ":" +
        String(now.getUTCMinutes()).padStart(2, "0") +
        " UTC";

      // ---- Inputs ----
      const meanDraft = this.calculationData.meanDraft;
      const trim = this.calculationData.trim;
      const windBft = this.calculationData.trueWindSpeed;
      const fouling = this.calculationData.fouling;
      const lhv = this.calculationData.lowerHeatingValue;

      // Wind direction mapping
      const windDirRaw = this.calculationData.windDirection;
      let windDirText = "";
      if (windDirRaw === 0) windDirText = "Head seas";
      else if (windDirRaw === 1) windDirText = "Beam seas";
      else if (windDirRaw === 2) windDirText = "Following seas";
      else windDirText = String(windDirRaw);

      // ---- Build Excel ----
      const aoa = [];

      // Metadata
      aoa.push(["Vessel", vessel]);
      aoa.push(["Exported at", exportTime]);
      aoa.push([
        "ME Actual FOC is based on the measured consumption of the vessel, " +
          "with the fuel used onboard over the last month. ",
      ]);
      aoa.push([
        "Aux. consumption is the average consumption over the last month",
      ]);
      aoa.push([
        "Auxiliary consumption (tn/day)",
        Number(this.auxiliaryConsumption).toFixed(2),
      ]);

      // Header row
      aoa.push([
        "Speed (kn)",
        "RPM",
        "Power (kW)",
        "ISO-Predicted (tn/day)",
        "Actual-Predicted (tn/day)",
        "ISO-Corrected (tn/day)",
        "Actual (tn/day)",
        "Mean Draft (m)",
        "Trim (m)",
        "Wind (BFT)",
        "Wind Direction",
        "Fouling",
        "LHV (kJ/kg)",
      ]);

      const auxValue = this.showConsumption ? this.auxiliaryConsumption : 0;

      this.tableItems.forEach((row, index) => {
  const isoValue = this.isoDailyConsumptionOfInterest[index]
  const measuredIsoValue = this.measuredIsoConsumptionOfInterest[index]
  const medianMeasuredValue = this.medianMeasuredConsumptionOfInterest[index]
  const actualExclAux = this.consumptionOfInterest[index]

  const isValidNum = (v) => v !== null && v !== undefined && !Number.isNaN(Number(v)) && Number(v) !== 0

  aoa.push([
    Number(row.speed).toFixed(1),
    this.formatValue(row.rpm, 1),
    isValidNum(row.power) ? Number(row.power).toFixed(0) : "-",
    this.formatValue(isoValue, 1),
    isValidNum(actualExclAux) ? (Number(actualExclAux) + auxValue).toFixed(1) : "-",
    this.formatValue(measuredIsoValue, 1),
    isValidNum(medianMeasuredValue) ? (Number(medianMeasuredValue) + auxValue).toFixed(1) : "-",
    meanDraft,
    trim,
    windBft,
    windDirText,
    fouling,
    lhv,
  ])
})

      const ws = XLSX.utils.aoa_to_sheet(aoa);

      ws["!cols"] = [
        { wch: 12 },
        { wch: 10 },
        { wch: 12 },
        { wch: 18 },
        { wch: 18 },
        { wch: 20 },
        { wch: 15 },
        { wch: 15 },
        { wch: 10 },
        { wch: 10 },
        { wch: 15 },
        { wch: 10 },
        { wch: 12 },
      ];

      ws["!freeze"] = { ySplit: 5 };

      const boldStyle = { font: { bold: true } };
      ws["A1"].s = boldStyle;
      ws["A2"].s = boldStyle;
      ws["A3"].s = boldStyle;

      const headerRowIndex = 5;
      for (let c = 0; c < 13; c++) {
        const cellAddr = XLSX.utils.encode_cell({ r: headerRowIndex, c });
        if (ws[cellAddr]) ws[cellAddr].s = boldStyle;
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Data Table");

      const filename = `QUAD_${vessel}_DataTable.xls`;
      const wbout = XLSX.write(wb, { bookType: "xls", type: "array" });

      const blob = new Blob([wbout], {
        type: "application/vnd.ms-excel",
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
/* Data table styling */
.v-data-table {
  background-color: transparent !important;
  color: var(--color-text) !important;
}

.table-scroll-container {
  flex: 1;
  min-height: 0;
  overflow-x: auto !important;
  overflow-y: hidden !important;
}

/* Table wrapper with forced width for horizontal scroll */
.table-wrapper {
  min-width: 500px;
  width: 100%;
  display: inline-block;
}

/* Group Header Rows */
.group-header-row {
  background: transparent !important;
}

.group-header-basic {
  background-color: transparent !important;
  border: none !important;
  padding: 12px 10px !important;
}

/* Extended Group Header Boxes */
.group-header-kdmodel-box,
.group-header-data-box {
  padding: 12px 10px !important;
  border-radius: 6px 6px 0 0 !important;
  vertical-align: middle !important;
}

.group-header-kdmodel-box {
  background: linear-gradient(135deg, rgba(100, 150, 255, 0.25), rgba(100, 150, 255, 0.12)) !important;
  border: 2px solid rgba(100, 150, 255, 0.5) !important;
  border-bottom: none !important;
}

.group-header-data-box {
  background: linear-gradient(135deg, rgba(100, 200, 150, 0.25), rgba(100, 200, 150, 0.12)) !important;
  border: 2px solid rgba(100, 200, 150, 0.5) !important;
  border-bottom: none !important;
}

.group-header-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 700 !important;
  font-size: 14px !important;
  color: var(--color-text) !important;
}

.group-icon {
  font-size: 18px !important;
}

.group-title {
  letter-spacing: 0.3px;
}

/* Column Header Row */
.column-header-row {
  background-color: var(--color-bg) !important;
}

.table-header {
  background-color: var(--color-bg) !important;
  color: var(--color-text) !important;
  border-bottom: 2px solid var(--color-border) !important;
  padding-left: 10px !important;
  padding-right: 10px !important;
  font-weight: 700 !important;
  font-size: 13px !important;
  height: 50px !important;
  vertical-align: middle !important;
}

/* Group-specific column header styling with extended boxes */
.table-header.group-basic {
  background-color: var(--color-bg) !important;
  border-right: 1px solid rgba(255, 255, 255, 0.1) !important;
}

.table-header.group-kdmodel {
  background: linear-gradient(to bottom, rgba(100, 150, 255, 0.15), rgba(100, 150, 255, 0.08)) !important;
  border: 2px solid rgba(100, 150, 255, 0.5) !important;
  border-top: none !important;
  border-right: 1px solid rgba(100, 150, 255, 0.4) !important;
}

.table-header.group-kdmodel:last-of-type {
  border-right: 2px solid rgba(100, 150, 255, 0.5) !important;
}

.table-header.group-data {
  background: linear-gradient(to bottom, rgba(100, 200, 150, 0.15), rgba(100, 200, 150, 0.08)) !important;
  border: 2px solid rgba(100, 200, 150, 0.5) !important;
  border-top: none !important;
  border-right: 1px solid rgba(100, 200, 150, 0.4) !important;
}

.table-header.group-data:last-of-type {
  border-right: 2px solid rgba(100, 200, 150, 0.5) !important;
}

/* Table rows */
.table-row {
  background-color: transparent !important;
}

/* Table data cells */
.table-row td {
  border-bottom: 1px solid var(--color-border) !important;
  color: var(--color-text) !important;
  padding-left: 10px !important;
  padding-right: 10px !important;
  height: 45px !important;
  text-align: center !important;
}

/* Basic group cells */
.table-row td.group-basic {
  background-color: var(--color-bg) !important;
  border-right: 1px solid rgba(255, 255, 255, 0.1) !important;
}

/* KD Model group cells - STRONG BLUE TINT */
.table-row td.group-kdmodel {
  background: linear-gradient(90deg, rgba(100, 150, 255, 0.12), rgba(100, 150, 255, 0.08)) !important;
  border: 1px solid rgba(100, 150, 255, 0.3) !important;
  border-right: 1px solid rgba(100, 150, 255, 0.25) !important;
}

/* Data group cells - STRONG GREEN TINT */
.table-row td.group-data {
  background: linear-gradient(90deg, rgba(100, 200, 150, 0.12), rgba(100, 200, 150, 0.08)) !important;
  border: 1px solid rgba(100, 200, 150, 0.3) !important;
  border-right: 1px solid rgba(100, 200, 150, 0.25) !important;
}

/* Row hover effects */
.table-row:hover td.group-basic {
  background-color: var(--color-overlay) !important;
}

/* Strong hover effect for KD Model */
.table-row:hover td.group-kdmodel {
  background: linear-gradient(90deg, rgba(100, 150, 255, 0.25), rgba(100, 150, 255, 0.18)) !important;
  box-shadow: inset 0 0 4px rgba(100, 150, 255, 0.2) !important;
}

/* Strong hover effect for Data */
.table-row:hover td.group-data {
  background: linear-gradient(90deg, rgba(100, 200, 150, 0.25), rgba(100, 200, 150, 0.18)) !important;
  box-shadow: inset 0 0 4px rgba(100, 200, 150, 0.2) !important;
}

/* Alternating rows - Odd */
.table-row.row-0 td.group-basic {
  background-color: var(--color-surface) !important;
}

.table-row.row-0 td.group-kdmodel {
  background: linear-gradient(90deg, rgba(100, 150, 255, 0.15), rgba(100, 150, 255, 0.1)) !important;
}

.table-row.row-0 td.group-data {
  background: linear-gradient(90deg, rgba(100, 200, 150, 0.15), rgba(100, 200, 150, 0.1)) !important;
}

/* Alternating rows - Even */
.table-row.row-1 td.group-basic {
  background-color: var(--color-bg) !important;
}

.table-row.row-1 td.group-kdmodel {
  background: linear-gradient(90deg, rgba(100, 150, 255, 0.08), rgba(100, 150, 255, 0.05)) !important;
}

.table-row.row-1 td.group-data {
  background: linear-gradient(90deg, rgba(100, 200, 150, 0.08), rgba(100, 200, 150, 0.05)) !important;
}
</style>
